/**
 * Global Type Definitions for Edouard.ai
 */

// ============================================
// Core Data Types
// ============================================

export interface Position {
  id: string;
  type: 'buy' | 'sell' | 'mining' | 'staking';
  date: string;
  quantity: number;
  price: number;
  note?: string;

  /**
   * Transaction fee in fiat (same currency as `price`). Optional — when
   * provided, the calculator includes it in cost basis (BUY: cost += fee;
   * SELL: proceeds -= fee). Existing positions without `fee` keep their
   * historical P&L unchanged because the calculator defaults to 0.
   *
   * UI to enter fees comes in P3 (Portfolio V2). For now, only programmatic
   * sources (CSV import, exchange sync) populate this field.
   */
  fee?: number;

  // ✨ Cost Basis Chain Tracking (swap lineage)
  original_cost_basis?: number;      // Prix d'acquisition initial en fiat (avant swaps)
  swap_from_position_id?: string;    // ID de la position source si créée par swap
  swap_chain_id?: string;            // ID unique pour tracer la chaîne de swaps
  is_swap?: boolean;                 // True si position créée/générée par un swap

  // 🌉 Cross-Chain Swap Fields (expert mode)
  source_chain?: string;             // Blockchain source (ethereum, bsc, polygon, arbitrum, etc.)
  destination_chain?: string;        // Blockchain destination
  bridge_protocol?: string;          // Protocole de bridge (stargate, wormhole, layerzero, hop, etc.)
  bridge_fee?: number;               // Frais de bridge en USD
  bridge_tx_hash?: string;           // Hash de transaction du bridge
  slippage_tolerance?: number;       // Tolérance de slippage en % (ex: 0.5, 1, 3)
  actual_slippage?: number;          // Slippage réel constaté en %
  swap_platform?: string;            // Plateforme de swap (uniswap, sushiswap, 1inch, etc.)
  gas_fee_usd?: number;              // Frais de gas en USD

  // 💎 Mining specific fields
  mining_pool?: string;              // Nom du pool de minage (ex: "Ethermine", "F2Pool")
  mining_hashrate?: number;          // Hashrate au moment du minage (MH/s ou TH/s)

  // 🔒 Staking specific fields
  staking_apy?: number;              // APY annuel (ex: 5.5 pour 5.5%)
  staking_duration?: number;         // Durée de staking en jours
  staking_cooldown?: number;         // Période de cooldown en jours avant libération
  staking_platform?: string;         // Plateforme de staking (ex: "Binance", "Kraken", "Native")
  staking_end_date?: string;         // Date de fin du staking (ISO format)
  staking_is_locked?: boolean;       // True si locked staking, false si flexible
}

export interface Token {
  id: string;
  displayName: string;
  positions: Position[];
  note?: string;
  tags?: string[];
  targetPrice?: number; // Optional target price for profit tracking
}

export interface Price {
  usd: number;
  usd_24h_change?: number;
  eur?: number;
  eur_24h_change?: number;
}

export interface Prices {
  [tokenId: string]: Price;
}

// ============================================
// Calculator Types
// ============================================

export interface CalculatorResult {
  realizedPL: number;
  remainingQty: number;
  avgCost: number;
  totalCost: number;
  method: 'FIFO' | 'LIFO' | 'AVERAGE_COST';
}

export type CalculationMethod = 'FIFO' | 'LIFO' | 'AVERAGE_COST';

export interface FIFOResults {
  [tokenId: string]: CalculatorResult;
}

// ============================================
// Component Props Types
// ============================================

export interface MetricsCardProps {
  label: string;
  value: string | number;
  sub?: string;
  color?: 'green' | 'red' | 'blue' | 'purple';
  tooltip?: string;
  icon?: React.ComponentType<{ size?: number; className?: string }>;
}

export interface PositionModalProps {
  isOpen: boolean;
  onClose: () => void;
  token: Token | { id: string; displayName: string };
  position: Position | null;
  onSave: (position: Partial<Position>) => void;
  prices: Prices;
}

// Cross-chain swap options for expert mode
export interface CrossChainSwapOptions {
  sourceChain?: string;
  destinationChain?: string;
  bridgeProtocol?: string;
  bridgeFee?: number;
  bridgeTxHash?: string;
  slippageTolerance?: number;
  actualSlippage?: number;
  swapPlatform?: string;
  gasFeeUsd?: number;
}

export interface SwapModalProps {
  isOpen: boolean;
  onClose: () => void;
  fromToken: Token;
  tokens: Token[];
  prices: Prices;
  onSwap: (
    fromId: string,
    toId: string,
    amount: number,
    feePct: number,
    date: string,
    crossChainOptions?: CrossChainSwapOptions
  ) => void;
  coins: Coin[];
  isExpertMode?: boolean;
}

export interface WhatIfSimulatorProps {
  isOpen: boolean;
  onClose: () => void;
  tokens: Token[];
  prices: Prices;
  fifoResults: FIFOResults;
  onApply: (tokenId: string, positionData: Partial<Position>) => void;
}

export interface NotesManagerProps {
  isOpen: boolean;
  onClose: () => void;
  tokens: Token[];
  onSaveNote: (tokenId: string, noteText: string) => void;
}

export interface LogViewerProps {
  isOpen: boolean;
  onClose: () => void;
  logs: Log[];
  onClear: () => void;
  onExport: (format: 'json' | 'txt', logs: Log[]) => void;
}

export interface PortfolioTableProps {
  tokens: Token[];
  prices: Prices;
  fifoResults: FIFOResults;
  onAddPosition: (token: Token) => void;
  onAddFirstToken?: () => void;
  onDeleteToken: (tokenId: string) => void;
  onEditPosition?: (token: Token, position: Position) => void;
  onDeletePosition?: (tokenId: string, positionId: string) => void;
  onSwapToken?: (token: Token) => void;
  onUpdateTarget?: (tokenId: string, targetPrice: number | null) => void;
  showBTCComparison?: boolean;
  btcHistoricalPrices?: Record<string, number>;
  btcCurrentPrice?: number;
  isNewToken?: (tokenId: string) => boolean;
  showTargetColumn?: boolean; // Show/hide Target price column
  isExpertMode?: boolean; // Show advanced swap details only in expert mode
}

export interface TokenInputProps {
  onAdd: (coinId: string, symbol: string) => void;
  coins: Coin[];
  map?: Record<string, unknown>;
}

export interface SpreadChartProps {
  tokens: Token[];
  isDark: boolean;
  days?: number;
  prices?: Prices;
  btcHistoricalPrices?: Record<string, number>;
  btcCurrentPrice?: number;
}

export interface PortfolioTimelineProps {
  tokens: Token[];
  prices: Prices;
  fifoResults: FIFOResults;
  isDark?: boolean;
}

// ============================================
// API & External Data Types
// ============================================

export interface Coin {
  id: string;
  symbol: string;
  name: string;
}

export interface CoinDetails extends Coin {
  market_cap_rank?: number;
  image?: string;
  current_price?: number;
  market_cap?: number;
  total_volume?: number;
  price_change_percentage_24h?: number;
}

export interface ChartDataPoint {
  time: number;
  value: number;
}

// ============================================
// Log Types
// ============================================

export type LogLevel = 'DEBUG' | 'INFO' | 'SUCCESS' | 'WARNING' | 'ERROR' | 'CRITICAL';

export interface Log {
  id: string | number;
  level: LogLevel;
  category: string;
  message: string;
  timestamp: number;
  data?: unknown;
}

// ============================================
// Toast Types
// ============================================

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export type ToastFunction = (message: string, type?: ToastType, duration?: number) => void;

// ============================================
// Storage Types
// ============================================

export interface AppState {
  tokens?: Token[];
  positions?: Position[];
  settings?: Record<string, unknown>;
  [key: string]: unknown;
}

// ============================================
// Icon Component Type
// ============================================

export interface IconProps {
  size?: number;
  className?: string;
  color?: string;
  style?: React.CSSProperties;
}

export type IconComponent = React.FC<IconProps>;

// ============================================
// Utility Types
// ============================================

export type Formatter = (value: number, decimals?: number) => string;

export interface ExportOptions {
  filename?: string;
  format?: 'csv' | 'json' | 'txt';
}

// ============================================
// Trading Bot (Autopilot) Types
// ============================================

export type BotProfile = 'conservative' | 'balanced' | 'aggressive' | 'custom';
export type TradingFrequency = 'low' | 'medium' | 'high';
export type SignalAction = 'buy' | 'sell' | 'hold';
export type SentimentType = 'bullish' | 'neutral' | 'bearish';
export type TrendType = 'up' | 'down' | 'stable';

export interface BotConfig {
  profile: BotProfile;
  maxPositionSize: number;         // 5-50% of portfolio
  stopLossPercent: number;         // 2-20%
  takeProfitPercent: number;       // 5-100%
  riskTolerance: number;           // 1-10 scale
  tradingFrequency: TradingFrequency;
  enableSentiment: boolean;
  enableTechnical: boolean;
  enableFundamental: boolean;
}

export interface TradingSignal {
  id: string;
  tokenId: string;
  tokenName: string;
  tokenSymbol: string;
  action: SignalAction;
  confidence: number;              // 0-100
  reason: string;                  // Human-readable explanation
  currentPrice: number;
  suggestedPrice?: number;         // Better entry point
  timeframe: string;               // '24-48h', '1-2 weeks', etc.
  timestamp: number;
  technicalScore?: number;         // 0-100
  sentimentScore?: number;         // 0-100
  riskLevel?: 'low' | 'medium' | 'high';
}

export interface SentimentData {
  source: string;                  // 'Twitter', 'Reddit', 'News', 'Technical'
  sentiment: SentimentType;
  score: number;                   // -100 to +100
  mentions: number;
  trend: TrendType;
  lastUpdate?: number;
}

export interface BotStats {
  totalSignals: number;
  winRate: number;                 // 0-100
  avgReturn: number;               // percentage
  activeTime: number;              // seconds since activation
  lastUpdate: number;              // timestamp
}

export interface AutopilotState {
  isActive: boolean;
  selectedProfile: BotProfile;
  config: BotConfig;
  signals: TradingSignal[];
  sentiments: SentimentData[];
  stats: BotStats;
}

// ============================================
// Trading Strategies Types
// ============================================

// Strategy Types - Aligned with Backend
export enum StrategyType {
  INFINITY_GRID = 'INFINITY_GRID',
  SMART_DCA = 'SMART_DCA',
  MA200_DCA = 'MA200_DCA',
  BTC_REBALANCER = 'BTC_REBALANCER',
  CUSTOM = 'CUSTOM',
}

export enum StrategyStatus {
  DRAFT = 'DRAFT',
  ACTIVE = 'ACTIVE',
  PAUSED = 'PAUSED',
  STOPPED = 'STOPPED',
  ERROR = 'ERROR',
}

export enum OrderType {
  MARKET = 'MARKET',
  LIMIT = 'LIMIT',
  STOP_LOSS = 'STOP_LOSS',
  TAKE_PROFIT = 'TAKE_PROFIT',
}

export enum OrderSide {
  BUY = 'BUY',
  SELL = 'SELL',
}

export enum OrderStatus {
  PENDING = 'PENDING',
  OPEN = 'OPEN',
  FILLED = 'FILLED',
  PARTIALLY_FILLED = 'PARTIALLY_FILLED',
  CANCELLED = 'CANCELLED',
  FAILED = 'FAILED',
}

export enum PositionStatus {
  OPEN = 'OPEN',
  CLOSED = 'CLOSED',
}

// Infinity Grid Strategy - Aligned with Backend
export interface InfinityGridConfig {
  symbol: string;                           // Trading pair e.g., 'BTC/USDT'
  baseAmount: number;                       // Base amount for first buy (e.g., 100 USDT)
  takeProfitPercent: number;                // Take profit percentage (e.g., 5%)
  dipLevels: number[];                      // Dip levels to buy at (e.g., [5, 10, 15, 20, 30])
  sizeMultipliers: number[];                // Size multipliers for each level (e.g., [1, 1.5, 2.5, 4, 6])
  maxPositions: number;                     // Maximum number of open positions (e.g., 5)
  maxExposurePercent: number;               // Maximum portfolio exposure (e.g., 50%)
  stopLossPercent: number;                  // Global stop loss (e.g., 25%)
  trailingStopPercent: number;              // Trailing stop (e.g., 2%)
  enableVolatilityAdaptation?: boolean;     // Auto-adjust grid based on volatility
  enableMarketRegimeDetection?: boolean;    // Adjust max positions based on market trend
  enableVolumeConfirmation?: boolean;       // Require volume spike for big dips
  rsiOverbought?: number;                   // RSI threshold for overbought (default: 70)
  minVolume?: number;                       // Minimum volume as % of average (default: 50%)
  exchange: string;                         // e.g., 'binance'
  testnet?: boolean;                        // Use testnet (default: false)
}

// Smart DCA Strategy - Aligned with Backend
export interface SmartDCAConfig {
  symbol: string;                   // Trading pair (e.g., 'BTC/USDT')
  baseAmount: number;               // Base DCA amount per purchase (USDT)
  schedule: DCASchedule;            // When to execute DCA
  totalBudget: number;              // Total budget allocated for DCA
  enableDipBuying: boolean;         // Buy more during dips
  dipThreshold: number;             // % drop to consider a dip (e.g., 5%)
  dipMultiplier: number;            // Multiply amount during dips (e.g., 2x)
  maxDipMultiplier: number;         // Max multiplier for big dips (e.g., 5x)
  reservePercent: number;           // % of budget to keep in reserve (e.g., 20%)
  useReserveForDips: boolean;       // Use reserve only for dips
  rsiOverbought: number;            // Pause DCA if RSI > this (e.g., 75)
  enableRSIPause: boolean;          // Enable RSI-based pausing
  maxPositionValue: number;         // Max total position value (USDT)
  // MA200 Boost (SmartDCA Enhanced)
  enableMA200Boost: boolean;        // Enable MA200-based boost
  ma200Timeframe: '1h' | '4h' | '1d' | '1w'; // Timeframe for MA200 calculation
  useWeightedMA: boolean;           // true = EMA200, false = SMA200
  ma200Threshold: number;           // % distance to trigger boost (e.g., 5 for ±5%)
  ma200Boost: number;               // Multiplier when near MA200 (e.g., 1.5 = +50%)
  ma200BelowBoost: number;          // Extra boost when BELOW MA200 (e.g., 2.0 = +100%)
  trackPerformance: boolean;        // Track DCA performance
  targetPrice?: number;             // Optional target price for exit
  testnet: boolean;                 // Use testnet for testing
  enabled: boolean;                 // Strategy enabled/disabled
}

export interface DCASchedule {
  frequency: 'daily' | 'weekly' | 'biweekly' | 'monthly';
  dayOfWeek?: number;               // 0-6 (Sunday-Saturday) for weekly
  dayOfMonth?: number;              // 1-31 for monthly
  time?: string;                    // HH:MM format (e.g., '09:00')
}

// MA200 DCA Strategy - Value Investing with MA200
export interface MA200DCAConfig {
  // Strategy identification
  strategyName: string;
  symbol: string;                   // e.g., 'BTC/USDT', 'HBAR/USDT'
  exchange: 'binance' | 'cryptocom';

  // MA200 Settings
  timeframe: '1h' | '4h' | '1d' | '1w';
  useWeightedMA: boolean;           // true = EMA200, false = SMA200
  ma200Threshold: number;           // % distance to trigger buys (e.g., 5 for ±5%)

  // Aggressiveness
  aggressivenessMultiplier: number; // Base multiplier (1.0 = normal, 2.0 = double)
  belowMA200Boost: number;          // Additional boost when price < MA200 (e.g., 1.5 = +50%)

  // Budget Management
  totalBudget: number;              // Total USDT budget
  minPurchaseAmount: number;        // Minimum purchase (e.g., 50)
  maxPurchaseAmount: number;        // Maximum purchase (e.g., 1000)
  reserveFund: number;              // % to keep in reserve (e.g., 20%)

  // Safety Limits
  maxDailyPurchases: number;        // Max purchases per day (e.g., 3)
  maxWeeklyPurchases: number;       // Max purchases per week (e.g., 10)
  pauseAboveMA200: boolean;         // Pause when price > MA200

  // Advanced
  combineWithRSI: boolean;          // Only buy if RSI < threshold
  rsiThreshold: number;             // RSI threshold (default: 40)
  minTimeBetweenPurchases: number;  // Minutes between purchases (e.g., 60)

  // Execution
  dryRun: boolean;                  // Test mode (no real orders)
}

// BTC Rebalancer Strategy - Aligned with Backend
export interface BTCRebalancerConfig {
  symbol: string;                           // Strategy identifier (e.g., 'BTC/USDT')
  targetBTCPercent: number;                 // Target BTC percentage (0-100)
  rebalanceThreshold: number;               // Rebalance when deviation exceeds this %
  minTimeBetweenRebalances: number;         // Minimum time between rebalances (hours)
  enableMomentumDetection: boolean;         // Enable momentum detection before rebalancing
  momentumIndicators: {
    useRSI: boolean;
    useSMA: boolean;
    useMACD: boolean;
    useVolume: boolean;
  };
  rsiThresholds: {
    bullish: number;                        // RSI > this = bullish (e.g., 60)
    bearish: number;                        // RSI < this = bearish (e.g., 40)
  };
  feeOptimization: {
    enabled: boolean;
    maxFeePercent: number;                  // Don't rebalance if fees > this %
    minProfitAfterFees: number;             // Minimum profit after fees to justify rebalance
  };
  adaptiveTarget: {
    enabled: boolean;
    adjustOnVolatility: boolean;            // Adjust target based on volatility
    volatilityThreshold: number;            // ATR threshold to trigger adjustment
    maxAdjustment: number;                  // Max % to adjust target
  };
  maxPositionValue: number;                 // Maximum total portfolio value
  maxSlippage: number;                      // Maximum acceptable slippage %
  testnet: boolean;                         // Dry run mode (testnet)
  enabled: boolean;                         // Enable/disable strategy
  trackPerformance: boolean;                // Track performance
}

// Trading Position (différent de Position pour portfolio)
export interface TradingPosition {
  id: string;
  strategyId: string;
  symbol: string;
  side: 'long' | 'short';
  entryPrice: number;
  quantity: number;
  currentPrice: number;
  unrealizedPnl: number;
  unrealizedPnlPercent: number;
  stopLossPrice?: number;
  takeProfitPrice?: number;
  status: PositionStatus;
  exchange: string;
  openedAt: number;
  closedAt?: number;
  metadata?: Record<string, unknown>;
}

// Order
export interface Order {
  id: string;
  positionId?: string;
  strategyId: string;
  exchange: string;
  exchangeOrderId?: string;
  symbol: string;
  type: OrderType;
  side: OrderSide;
  price?: number;
  quantity: number;
  filledQuantity: number;
  averageFillPrice?: number;
  status: OrderStatus;
  fee?: number;
  feeCurrency?: string;
  createdAt: number;
  executedAt?: number;
  metadata?: Record<string, unknown>;
}

// Strategy Instance
export interface Strategy {
  id: string;
  userId: string;
  name: string;
  type: StrategyType;
  description?: string;
  config: InfinityGridConfig | SmartDCAConfig | BTCRebalancerConfig;
  status: StrategyStatus;
  createdAt: number;
  startedAt?: number;
  stoppedAt?: number;
}

// ============================================
// Backtesting Types
// ============================================

export interface BacktestConfig {
  strategyType: StrategyType;
  strategyConfig: InfinityGridConfig | SmartDCAConfig | BTCRebalancerConfig;
  symbol: string;
  timeframe: '1m' | '5m' | '15m' | '1h' | '4h' | '1d';
  startDate: number;                // timestamp
  endDate: number;                  // timestamp
  initialCapital: number;           // USDT
}

export interface BacktestResult {
  id: string;
  config: BacktestConfig;
  finalCapital: number;
  totalReturnPercent: number;
  totalTrades: number;
  winningTrades: number;
  losingTrades: number;
  winRate: number;                  // 0-100
  avgWin: number;
  avgLoss: number;
  profitFactor: number;             // avgWin / avgLoss
  sharpeRatio: number;
  maxDrawdown: number;
  maxDrawdownPercent: number;
  equityCurve: { time: number; value: number }[];
  trades: BacktestTrade[];
  status: 'running' | 'completed' | 'failed';
  createdAt: number;
  completedAt?: number;
}

export interface BacktestTrade {
  time: number;
  side: OrderSide;
  price: number;
  quantity: number;
  pnl: number;
  pnlPercent: number;
  balance: number;
}

// ============================================
// Performance Analytics Types
// ============================================

export interface StrategyPerformance {
  strategyId: string;
  date: string;                     // YYYY-MM-DD
  totalTrades: number;
  winningTrades: number;
  losingTrades: number;
  totalPnl: number;
  totalPnlPercent: number;
  winRate: number;
  avgWin: number;
  avgLoss: number;
  profitFactor: number;
  sharpeRatio: number;
  maxDrawdown: number;
  maxDrawdownPercent: number;
  roi: number;
}

export interface PortfolioSnapshot {
  timestamp: number;
  totalValueUSDT: number;
  availableBalance: number;
  lockedBalance: number;
  unrealizedPnl: number;
  realizedPnl: number;
  totalPnl: number;
  totalPnlPercent: number;
  assets: Record<string, number>;   // { 'BTC': 0.5, 'ETH': 2.3 }
}

// ============================================
// Risk Management Types
// ============================================

export interface RiskSettings {
  maxPositionSizePercent: number;   // % of portfolio (default: 5%)
  maxTotalExposurePercent: number;  // % of portfolio (default: 80%)
  maxDailyLossPercent: number;      // % (default: 10%)
  maxDrawdownPercent: number;       // % (default: 20%)
  stopAllOnDailyLoss: boolean;      // default: true
  allowedLeverage: number;          // default: 1.0 (no leverage)
  blacklistedSymbols: string[];     // ['DOGE', 'SHIB']
  whitelistedSymbols: string[];     // ['BTC', 'ETH', 'SOL']
  tradingHours?: {
    start: string;                  // '00:00'
    end: string;                    // '23:59'
  };
}

export interface RiskEvent {
  id: string;
  userId: string;
  strategyId?: string;
  eventType: string;                // 'max_loss_reached', 'drawdown_limit', etc.
  severity: 'info' | 'warning' | 'critical';
  message: string;
  actionTaken?: string;             // 'stopped_strategy', 'closed_positions', etc.
  metadata?: Record<string, unknown>;
  createdAt: number;
}

// ============================================
// Social Trading Types
// ============================================

export interface StrategyTemplate {
  id: string;
  creatorId: string;
  name: string;
  type: StrategyType;
  description: string;
  defaultConfig: InfinityGridConfig | SmartDCAConfig | BTCRebalancerConfig;
  minCapital: number;
  expectedAnnualReturn: number;     // %
  riskScore: number;                // 1-10
  totalCopies: number;
  totalLikes: number;
  isVerified: boolean;
  createdAt: number;
}

export interface LeaderboardEntry {
  userId: string;
  username: string;
  totalReturn: number;              // %
  winRate: number;                  // %
  sharpeRatio: number;
  followers: number;
  isVerified: boolean;
  rank: number;
}
